import { ArrowLeftRight, MoreHorizontal, Paperclip, Pencil, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router";
import type { Account, Category, Transaction } from "@/api/types";
import { CategoryIcon } from "@/components/CategoryIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";

/** Um lançamento: ícone da categoria, descrição, categoria/conta e valor com sinal; menu ⋯ com editar/excluir. */
export function TransactionItem({
  tx,
  wid,
  accounts,
  categories,
  onDelete,
}: {
  tx: Transaction;
  wid: string;
  accounts?: Account[];
  categories?: Category[];
  /** Sem `onDelete` (ou sem permissão), o item não tem menu de ações. */
  onDelete?: (tx: Transaction) => void;
}) {
  const navigate = useNavigate();
  const account = accounts?.find((a) => a.id === tx.accountId)?.name;
  const category = categories?.find((c) => c.id === tx.categoryId) ?? null;
  const transfer = tx.type === "transfer";
  const label = transfer
    ? tx.transferDirection === "out"
      ? "Transferência enviada"
      : "Transferência recebida"
    : category?.name;
  const incoming = tx.type === "income" || tx.transferDirection === "in";
  const href = `/w/${wid}/transactions/${tx.id}`;

  return (
    <li className="flex items-center gap-3 border-b border-border py-3 last:border-0">
      {transfer ? (
        <span
          aria-hidden="true"
          className="flex size-10 shrink-0 items-center justify-center rounded-control bg-surface-2 text-muted"
        >
          <ArrowLeftRight className="size-5" />
        </span>
      ) : (
        <CategoryIcon category={category} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <Link to={href} className="block truncate text-[15px] font-medium hover:underline">
            {tx.description}
          </Link>
          {/* Só o indicador: a imagem do comprovante nunca aparece (nem é baixada) na lista (#37). */}
          {tx.attachment && (
            <Paperclip role="img" aria-label="Tem comprovante" className="size-3.5 shrink-0 text-muted" />
          )}
        </div>
        <p className="m-0 flex items-center gap-1.5 truncate text-xs text-muted">
          {[label, account].filter(Boolean).join(" · ")}
          {tx.status === "pending" && (
            <span className="rounded-full bg-warning-soft px-1.5 py-px font-medium text-warning">
              pendente
            </span>
          )}
        </p>
      </div>
      <span
        className={cn(
          "shrink-0 text-[15px] font-semibold tabular-nums",
          transfer ? "text-ink" : incoming ? "text-income" : "text-expense",
        )}
      >
        {incoming ? "+ " : "− "}
        {formatMoney(tx.amountMinor, tx.currency)}
      </span>
      {onDelete && (
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={`Ações de ${tx.description}`}
            className="-mr-2 flex size-10 shrink-0 items-center justify-center rounded-control text-muted hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
          >
            <MoreHorizontal aria-hidden="true" className="size-5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <DropdownMenuItem onSelect={() => navigate(href)}>
              <Pencil aria-hidden="true" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onDelete(tx)} className="text-expense [&_svg]:text-expense">
              <Trash2 aria-hidden="true" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
}
